import React, { useState, useRef } from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import { X, ChevronDown } from 'lucide-react';

// Catégories de symboles
const categories = {
  fractions: {
    icon: '½',
    label: 'Fractions',
    items: [
      { label: '▢/▢', latex: '\\frac{▢}{▢}', desc: 'Fraction' },
      { label: '½', latex: '\\frac{1}{2}', desc: 'Un demi' },
      { label: '⅓', latex: '\\frac{1}{3}', desc: 'Un tiers' },
      { label: '¼', latex: '\\frac{1}{4}', desc: 'Un quart' },
      { label: '¾', latex: '\\frac{3}{4}', desc: 'Trois quarts' },
    ]
  },
  puissances: {
    icon: 'x²',
    label: 'Puissances',
    items: [
      { label: 'x▢', latex: '^{▢}', desc: 'Exposant' },
      { label: 'x²', latex: '^2', desc: 'Au carré' },
      { label: 'x³', latex: '^3', desc: 'Au cube' },
      { label: 'xⁿ', latex: '^{n}', desc: 'Puissance n' },
      { label: '10²', latex: '10^2', desc: 'Cent' },
      { label: '10³', latex: '10^3', desc: 'Mille' },
    ]
  },
  racines: {
    icon: '√',
    label: 'Racines',
    items: [
      { label: '√▢', latex: '\\sqrt{▢}', desc: 'Racine carrée' },
      { label: '√2', latex: '\\sqrt{2}', desc: 'Racine de 2' },
      { label: '√3', latex: '\\sqrt{3}', desc: 'Racine de 3' },
      { label: '∛▢', latex: '\\sqrt[3]{▢}', desc: 'Racine cubique' },
      { label: 'ⁿ√▢', latex: '\\sqrt[n]{▢}', desc: 'Racine n-ième' },
    ]
  },
  indices: {
    icon: 'xₙ',
    label: 'Indices',
    items: [
      { label: 'x▢', latex: '_{▢}', desc: 'Indice' },
      { label: 'x₁', latex: '_{1}', desc: 'Indice 1' },
      { label: 'x₂', latex: '_{2}', desc: 'Indice 2' },
      { label: 'xₙ', latex: '_{n}', desc: 'Indice n' },
      { label: 'uₙ', latex: 'u_{n}', desc: 'Suite u' },
      { label: 'uₙ₊₁', latex: 'u_{n+1}', desc: 'Suite u n+1' },
    ]
  },
  symboles: {
    icon: 'π',
    label: 'Symboles',
    items: [
      { label: 'π', latex: '\\pi', desc: 'Pi' },
      { label: '∞', latex: '\\infty', desc: 'Infini' },
      { label: '≠', latex: '\\neq', desc: 'Différent' },
      { label: '≈', latex: '\\approx', desc: 'Environ' },
      { label: '±', latex: '\\pm', desc: 'Plus ou moins' },
      { label: '∈', latex: '\\in', desc: 'Appartient' },
      { label: '∉', latex: '\\notin', desc: "N'appartient pas" },
      { label: '⊂', latex: '\\subset', desc: 'Inclus' },
      { label: '∪', latex: '\\cup', desc: 'Union' },
      { label: '∩', latex: '\\cap', desc: 'Intersection' },
    ]
  },
  comparaisons: {
    icon: '≤',
    label: 'Comparaisons',
    items: [
      { label: '<', latex: '<', desc: 'Inférieur' },
      { label: '>', latex: '>', desc: 'Supérieur' },
      { label: '≤', latex: '\\leq', desc: 'Inférieur ou égal' },
      { label: '≥', latex: '\\geq', desc: 'Supérieur ou égal' },
      { label: '=', latex: '=', desc: 'Égal' },
      { label: '≠', latex: '\\neq', desc: 'Différent' },
    ]
  },
  operations: {
    icon: '×',
    label: 'Opérations',
    items: [
      { label: '+', latex: '+', desc: 'Addition' },
      { label: '−', latex: '-', desc: 'Soustraction' },
      { label: '×', latex: '\\times', desc: 'Multiplication' },
      { label: '÷', latex: '\\div', desc: 'Division' },
      { label: '·', latex: '\\cdot', desc: 'Point' },
      { label: ':', latex: ':', desc: 'Ratio' },
    ]
  },
  lettres: {
    icon: 'α',
    label: 'Lettres',
    items: [
      { label: 'α', latex: '\\alpha', desc: 'Alpha' },
      { label: 'β', latex: '\\beta', desc: 'Beta' },
      { label: 'γ', latex: '\\gamma', desc: 'Gamma' },
      { label: 'δ', latex: '\\delta', desc: 'Delta' },
      { label: 'θ', latex: '\\theta', desc: 'Theta' },
      { label: 'λ', latex: '\\lambda', desc: 'Lambda' },
      { label: 'μ', latex: '\\mu', desc: 'Mu' },
      { label: 'σ', latex: '\\sigma', desc: 'Sigma' },
      { label: 'φ', latex: '\\phi', desc: 'Phi' },
      { label: 'ω', latex: '\\omega', desc: 'Omega' },
    ]
  },
  geometrie: {
    icon: '△',
    label: 'Géométrie',
    items: [
      { label: '∠ABC', latex: '\\widehat{ABC}', desc: 'Angle' },
      { label: '⊥', latex: '\\perp', desc: 'Perpendiculaire' },
      { label: '∥', latex: '\\parallel', desc: 'Parallèle' },
      { label: '→AB', latex: '\\overrightarrow{AB}', desc: 'Vecteur' },
      { label: '[AB]', latex: '[AB]', desc: 'Segment' },
      { label: '(AB)', latex: '(AB)', desc: 'Droite' },
      { label: '°', latex: '^{\\circ}', desc: 'Degré' },
      { label: '|x|', latex: '|x|', desc: 'Valeur absolue' },
    ]
  },
  ensembles: {
    icon: 'ℕ',
    label: 'Ensembles',
    items: [
      { label: 'ℕ', latex: '\\mathbb{N}', desc: 'Entiers naturels' },
      { label: 'ℤ', latex: '\\mathbb{Z}', desc: 'Entiers relatifs' },
      { label: 'ℚ', latex: '\\mathbb{Q}', desc: 'Rationnels' },
      { label: 'ℝ', latex: '\\mathbb{R}', desc: 'Réels' },
      { label: 'ℂ', latex: '\\mathbb{C}', desc: 'Complexes' },
      { label: '∅', latex: '\\emptyset', desc: 'Ensemble vide' },
    ]
  },
};

// Templates de formules rapides
const templates = [
  { label: 'Espace', latex: '\\;' },
  { label: 'Éq. 1er degré', latex: 'ax + b = 0' },
  { label: 'Distributivité', latex: 'k(a + b) = ka + kb' },
  { label: 'Double distrib.', latex: '(a+b)(c+d) = ac + ad + bc + bd' },
  { label: 'Pythagore', latex: 'a^2 + b^2 = c^2' },
  { label: 'Thalès', latex: '\\frac{AM}{AB} = \\frac{AN}{AC} = \\frac{MN}{BC}' },
  { label: 'Proportionnalité', latex: '\\frac{a}{b} = \\frac{c}{d}' },
  { label: 'Pourcentage', latex: '\\frac{x}{100} \\times N' },
];

const ExpressionNumeriqueElement = ({ config, onConfigChange, onRemove, isPreview = false }) => {
  const [activeCategory, setActiveCategory] = useState('fractions');
  const [showTemplates, setShowTemplates] = useState(false);
  const inputRef = useRef(null);

  const contenu = config?.contenu || '';

  // Mode preview : affiche juste le rendu KaTeX
  if (isPreview) {
    if (!contenu) return null;

    return (
      <div className="bg-gradient-to-r from-slate-50 to-blue-50 border border-blue-200 rounded-xl p-4 my-3">
        <div className="flex flex-col items-center justify-center text-sm overflow-x-auto">
          {contenu.split('\n').map((line, idx) => {
            const trimmed = line.trim();
            if (!trimmed) return <div key={idx} className="h-2" />;

            try {
              const displayText = trimmed.replace(/▢/g, '\\square');
              return (
                <div key={idx} className="my-1">
                  <BlockMath math={displayText} />
                </div>
              );
            } catch {
              return <p key={idx} className="text-gray-700">{trimmed}</p>;
            }
          })}
        </div>
      </div>
    );
  }

  // Fonction pour insérer du LaTeX
  const insertLatex = (latex) => {
    if (!inputRef.current) return;

    const input = inputRef.current;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const text = contenu;

    const newText = text.slice(0, start) + latex + text.slice(end);
    onConfigChange({ ...config, contenu: newText });

    setTimeout(() => {
      const placeholderIndex = newText.indexOf('▢', start);
      if (placeholderIndex !== -1) {
        input.setSelectionRange(placeholderIndex, placeholderIndex + 1);
      } else {
        const newPos = start + latex.length;
        input.setSelectionRange(newPos, newPos);
      }
      input.focus();
    }, 0);
  };

  // Rendu de l'aperçu
  const renderPreview = () => {
    if (!contenu.trim()) {
      return <span className="text-gray-400 italic text-xs">Aperçu LaTeX</span>;
    }

    return contenu.split('\n').map((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) return <div key={idx} className="h-2" />;

      try {
        const displayText = trimmed.replace(/▢/g, '\\square');
        return (
          <div key={idx} className="my-0.5">
            <InlineMath math={displayText} />
          </div>
        );
      } catch {
        return <span key={idx} className="text-red-500 text-xs">{trimmed}</span>;
      }
    });
  };

  return (
    <div className="border border-sky-200 rounded-xl overflow-hidden bg-white">
      {/* Input + Aperçu intégré */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={contenu}
          onChange={(e) => onConfigChange({ ...config, contenu: e.target.value })}
          placeholder="Saisissez votre formule ici..."
          className="w-full px-3 py-2.5 text-sm font-mono text-sky-900 bg-white border-b border-sky-100 focus:outline-none focus:bg-white placeholder-sky-300 transition"
        />
        {contenu && (
          <button
            onClick={() => onConfigChange({ ...config, contenu: '' })}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-sky-300 hover:text-sky-600 transition"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {/* Aperçu KaTeX */}
      <div className="px-3 py-2.5 bg-sky-50/60 min-h-[36px] flex items-center justify-center text-base text-gray-800 border-b border-sky-100">
        {renderPreview()}
      </div>

      {/* Catégories icônes seules */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-sky-100">
        {Object.entries(categories).map(([key, cat]) => (
          <button
            key={key}
            onClick={() => setActiveCategory(key)}
            title={cat.label}
            className={`w-8 h-7 rounded-md flex items-center justify-center text-xs font-medium transition-all ${
              activeCategory === key
                ? 'bg-sky-500 text-white shadow-sm'
                : 'text-gray-500 hover:bg-sky-100 hover:text-sky-700'
            }`}
          >
            {cat.icon}
          </button>
        ))}
      </div>

      {/* Grille de symboles compacte */}
      <div className="flex flex-wrap gap-1 px-2 py-2">
        {categories[activeCategory].items.map((item, idx) => (
          <button
            key={idx}
            onClick={() => insertLatex(item.latex)}
            title={item.desc}
            className="w-9 h-8 rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-sky-50 hover:border-sky-300 hover:text-sky-700 transition-all"
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Templates collapsible */}
      <div className="px-2 pb-2">
        <button
          onClick={() => setShowTemplates(!showTemplates)}
          className="flex items-center gap-1 text-[10px] font-medium text-gray-400 hover:text-gray-600 transition"
        >
          <ChevronDown size={11} className={`transition-transform ${showTemplates ? 'rotate-0' : '-rotate-90'}`} />
          Formules rapides
        </button>
        {showTemplates && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {templates.map((tpl, idx) => (
              <button
                key={idx}
                onClick={() => insertLatex(tpl.latex)}
                className="px-2 py-1 bg-gray-50 border border-gray-200 rounded-md text-[10px] text-gray-500 hover:bg-sky-50 hover:text-sky-700 hover:border-sky-200 transition"
              >
                {tpl.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpressionNumeriqueElement;
